// Single source of truth for all demo-tutorial copy: the login screen, the
// guided tour (welcome + steps), and every info tooltip.
//
// The tour-step bodies and TIPS values are trusted, static HTML strings —
// driver.js renders them via innerHTML and <InfoTip> via dangerouslySetInnerHTML,
// so <strong>, <ul>/<li>, and <span class="accent"> (orange) all render.
// Keep them tight: lead with the business value, use bullets and bold.

// Plain-text version of the problem, used in the login "About" panel.
export const THE_PROBLEM =
  'Freight can be half the cost of a bulk-materials load — a $35/yd load of mulch can carry ' +
  '$200+ of delivery on a long haul. So two questions run the business: what does a load cost ' +
  'delivered, and which yard should haul it? Cornerstone answers both, live, from your own ' +
  'delivery history.';

// Login screen helper strings.
export const LOGIN = {
  tagline: 'Delivered-pricing for a bulk-materials supplier.',
  whatYoullDo: [
    'Watch yard prices update live across the region',
    'Find the cheapest yard that can actually fill the load',
    'Place a delivery order — by chat or one click — and print the ticket',
    'Track every order from open to filled on one board',
  ],
  emailHint: "Enter any email. We'll send a one-time code (new emails are created automatically).",
  codeHint: 'Check your inbox for the 6–8 digit code.',
  footer: "You'll land in a shared demo dataset — explore freely, then hit “Take a tour” up top.",
};

// Guided tour — Welcome step + spotlight steps.
export const WELCOME = {
  title: 'Welcome to Cornerstone',
  body:
    "<p>Freight can be <strong>half the cost</strong> of a bulk-materials load — a $35/yd load of " +
    "mulch can carry <span class='accent'>$200+ of delivery</span>.</p>" +
    '<p>Two questions run the business:</p>' +
    '<ul><li><strong>What does a load cost delivered?</strong></li>' +
    '<li><strong>Which yard should haul it?</strong></li></ul>' +
    "<p>This app answers both live — then turns the answer into an order. " +
    "Here's the quick tour.</p>",
};

export interface TourStepCopy {
  selector?: string; // data-tour value; omit for the centered Welcome step
  title: string;
  body: string; // HTML (rendered as the driver popover description)
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
}

export const TOUR_STEPS: TourStepCopy[] = [
  {
    selector: 'material-filter',
    title: '1 · Pick a material',
    body:
      "Choose what you're pricing. <strong>Prices, map, and rankings all re-compute instantly</strong> " +
      'for this material.',
    side: 'bottom',
    align: 'start',
  },
  {
    selector: 'yard-prices',
    title: '2 · Live yard prices',
    body:
      'Your price at each yard, editable in place.' +
      '<ul><li><strong>Click</strong> a cell to edit, then Submit.</li>' +
      "<li>Cells turn <span class='accent'>amber / red</span> when a price goes stale — so the desk " +
      'never quotes off old numbers.</li></ul>',
    side: 'right',
    align: 'start',
  },
  {
    selector: 'home-map',
    title: '3 · See the spread',
    body:
      'We double-clicked a yard for you. <strong>Delivered price fans out across the map</strong> — ' +
      'green near the yard, climbing with freight as you go. ' +
      "<span class='accent'>Right-click any site to start an order on the spot.</span>",
    side: 'left',
    align: 'center',
  },
  {
    selector: 'cheapest-panel',
    title: '4 · Win the job — and protect stock',
    body:
      'The money question: <strong>which yard delivers cheapest?</strong> We picked a site for you.' +
      '<ul><li>Every yard is <strong>ranked by delivered price</strong>, the winner badged ' +
      "<span class='accent'>Cheapest</span>.</li>" +
      "<li>Set <strong>Needed</strong> qty — yards that can't cover it dim out, so the badge always " +
      'lands on the cheapest yard that <em>can actually fill the load</em>. No overselling stock.</li></ul>',
    side: 'top',
    align: 'start',
  },
  {
    selector: 'cheapest-panel',
    title: '5 · One click → a delivery ticket',
    body:
      'Hit <strong>Order</strong> on any yard and we draft a <strong>delivery ticket</strong> — yard, ' +
      'material, qty, and delivered price prefilled. ' +
      "<span class='accent'>Save it, or print the PDF to send straight to the yard.</span>",
    side: 'top',
    align: 'start',
  },
  {
    selector: 'orders',
    title: '6 · The orders board',
    body:
      'Every order lands here and the whole desk sees the same board.' +
      "<ul><li>Track each one <span class='accent'>Open → In progress → Filled</span>.</li>" +
      '<li>Filter by status at a glance — <strong>nothing slips through the cracks</strong>.</li></ul>',
    side: 'top',
    align: 'start',
  },
  {
    selector: 'ai',
    title: '7 · Just say it',
    body:
      'Type plain English — <em>“20 yards mulch to 123 Oak St”</em> — and the assistant ' +
      '<strong>logs orders &amp; updates prices</strong> for you. It proposes; you confirm.<br>' +
      "<span class='accent'>That's the tour — now explore, and hover any ⓘ for the detail.</span>",
    side: 'right',
    align: 'center',
  },
];

// Info tooltip strings (the ⓘ popovers) — HTML, value-first.
export const TIPS = {
  // YardPricesGrid
  yardPriceHeader:
    '<strong>Your price at each yard</strong>, per unit, before delivery.' +
    '<ul><li>Click a cell to edit.</li><li>Double-click a yard to map it.</li></ul>',
  colPrice:
    'Price at the yard, before freight.<br>' +
    "<span class='accent'>Amber = stale &gt;48h, red &gt;96h</span> — so quotes never run on old numbers.",
  // CheapestYardPanel
  cheapestPanel:
    '<strong>Which yard delivers cheapest here?</strong> Pick a site and every yard is ranked by ' +
    'delivered price. The winner gets the job.',
  colMaterialPrice: 'Base price at the yard, <strong>before freight</strong>.',
  colDist: 'Real driving miles from the yard to the selected site.',
  colFreight:
    '<strong>Delivery cost per unit.</strong><br>(distance × $/mile) ÷ units per load.<br>' +
    'The $/mile comes from your fitted freight curve.',
  colInv:
    "<strong>On-hand inventory</strong> at the yard, in the material's own unit (cubic yards or tons). " +
    "Shown in <span class='accent'>red when it's below what you need</span> — that yard can't fill the " +
    'order, so the cheapest yard that <em>can</em> is badged instead.',
  colDelivered:
    '<strong>The number that wins jobs.</strong><br>Material + freight = what you quote the customer. ' +
    "<span class='accent'>Compare across yards to protect margin.</span>",
  cheapestBadge:
    '<strong>Lowest delivered price</strong> for this site &amp; material — the yard that should haul it.',
  // Settings
  freightCurve:
    '<strong>cost_per_mile = a + b ÷ distance</strong>, fitted from your delivery history.<br>' +
    'Short hauls cost more per mile — a truck’s fixed loading cost spreads over fewer miles. ' +
    "<span class='accent'>Pricing reflects reality, not a flat guess.</span>",
} as const;
